import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavBar from "./components/NavBar";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import SeriesTicker from "./components/SeriesTicker";
import CommandPalette from "./components/CommandPalette";
import { initAnalytics, trackPageview, trackPageviewInternal } from "./lib/analytics";

const Hero = lazy(() => import("./components/Hero"));
const HomeSeries = lazy(() => import("./components/HomeSeries"));
const HomeVideos = lazy(() => import("./components/HomeVideos"));
const Proyectos = lazy(() => import("./components/Proyectos"));
const ProyectoDetalle = lazy(() => import("./components/ProyectoDetalle"));
const ContenidoHub = lazy(() => import("./components/ContenidoHub"));
const ContenidoSerie = lazy(() => import("./components/ContenidoSerie"));
const ContenidoArticulo = lazy(() => import("./components/ContenidoArticulo"));
const DominaClaude = lazy(() => import("./components/DominaClaude"));
const About = lazy(() => import("./components/About"));
const Services = lazy(() => import("./components/Services"));
const Contact = lazy(() => import("./components/Contact"));
const AIAssistant = lazy(() => import("./components/AIAssistant"));
const SocialFeed = lazy(() => import("./components/SocialFeed"));
const Booking = lazy(() => import("./components/Booking"));
const VideoInsights = lazy(() => import("./components/VideoInsights"));
const NotFound = lazy(() => import("./components/NotFound"));
const AdminPage = lazy(() => import("./admin/AdminPage"));


function PageLoader() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: "2px solid var(--accent-10)",
          borderTopColor: "var(--color-accent)",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

function PublicLayout() {
  const { t } = useTranslation();
  return (
    <>
      <a
        href="#main-content"
        style={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
        onFocus={(e) => {
          e.target.style.position = "fixed";
          e.target.style.top = "8px";
          e.target.style.left = "8px";
          e.target.style.width = "auto";
          e.target.style.height = "auto";
          e.target.style.overflow = "visible";
          e.target.style.zIndex = "9999";
          e.target.style.padding = "12px 24px";
          e.target.style.background = "var(--color-accent)";
          e.target.style.color = "#080810";
          e.target.style.borderRadius = "8px";
          e.target.style.fontFamily = "var(--font-body)";
          e.target.style.fontWeight = "700";
          e.target.style.fontSize = "14px";
        }}
        onBlur={(e) => {
          e.target.style.position = "absolute";
          e.target.style.left = "-9999px";
          e.target.style.width = "1px";
          e.target.style.height = "1px";
          e.target.style.overflow = "hidden";
        }}
      >
        {t("a11y.skipToContent")}
      </a>
      <NavBar />
      <CommandPalette />
      <main id="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<><Hero /><SeriesTicker /><HomeSeries /><HomeVideos /></>} />
            <Route path="/proyectos" element={<Proyectos />} />
            <Route path="/proyectos/:slug" element={<ProyectoDetalle />} />
            <Route path="/contenido" element={<ContenidoHub />} />
            <Route path="/contenido/serie/:slug" element={<ContenidoSerie />} />
            {/* Artículos on-site (F2) — el segmento estático "serie" gana precedencia */}
            <Route path="/contenido/:slug" element={<ContenidoArticulo />} />
            <Route path="/domina-claude" element={<DominaClaude />} />
            {/* Legacy: /blog redirige a /contenido (preservar SEO + backlinks viejos) */}
            <Route path="/blog" element={<Navigate to="/contenido" replace />} />
            <Route path="/sobre-mi" element={<About />} />
            <Route path="/asistente" element={<AIAssistant />} />
            <Route path="/servicios" element={<Services />} />
            <Route path="/redes" element={<SocialFeed />} />
            <Route path="/insights" element={<VideoInsights />} />
            <Route path="/agendar" element={<Booking />} />
            <Route path="/contacto" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageview(location.pathname); // GA4 (si hay ID)
    trackPageviewInternal(location.pathname); // panel propio (siempre)
  }, [location.pathname]);

  return (
    <ErrorBoundary>
      {isAdmin ? (
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/admin/*" element={<AdminPage />} />
          </Routes>
        </Suspense>
      ) : (
        <PublicLayout />
      )}
    </ErrorBoundary>
  );
}
